/**
 * Advertiser report pages API client — the advertiser level of the Ad Ops
 * "Campaign Reports" tab.
 * Backend: /api/ad-ops/advertiser-reports (requires adOps.campaignReports.manage).
 *
 * An advertiser page groups every GAM order of one advertiser (plus aliases /
 * manual includes) into one passcode-protected hub at
 * culturegenesis.com/advertisers/{slug}/reports. Creation and "Sync now" run
 * discovery + child-page creation + report generation DETACHED — the UI polls
 * the list while `sync.status === 'running'`.
 *
 * Types mirror backend/src/types/advertiserReports.ts and the admin route's
 * decorated payloads.
 */

import { apiRequest } from './api';
import type { ApiError } from './api';
import type { AdvertiserCampaignLifecycle, CampaignReportPage, ReportableOrder } from './campaignReportsApi';

export type { AdvertiserCampaignLifecycle } from './campaignReportsApi';

const BASE = '/api/ad-ops/advertiser-reports';

export type AdvertiserSyncStatus = 'idle' | 'running' | 'error';

export type AdvertiserSyncStep = 'idle' | 'discovering' | 'linking' | 'creating' | 'generating' | 'done';

export type AdvertiserSyncSkipReason =
  /** Unlimited-flight order — a page needs an explicit reporting window (Manage → Create with reporting window). */
  | 'needs_reporting_window'
  /** DRAFT / PENDING_APPROVAL in GAM — admin-only until approved. */
  | 'awaiting_approval'
  /** Lifecycle not client-visible for this page (e.g. upcoming with showUpcoming off). */
  | 'not_visible'
  /** Page creation threw. */
  | 'create_failed';

export type AdvertiserSyncWarningReason =
  /** Linked an existing page whose reporting window is narrower than the flight. */
  | 'month_slice_linked'
  /** Discovery hit the 500-row PQL cap — some orders may be missing. */
  | 'discovery_truncated'
  /** Two living pages for one order after the create phase — the sync-created one was revoked. */
  | 'duplicate_page_revoked'
  /** A manual include belongs to a different GAM advertiser. */
  | 'cross_advertiser_include'
  /** Order lookup failed for a manual include. */
  | 'order_lookup_failed'
  /** The order's living page is linked to ANOTHER advertiser page — left alone. */
  | 'linked_elsewhere';

export type AdvertiserSyncReason = AdvertiserSyncSkipReason | AdvertiserSyncWarningReason;

export interface AdvertiserSyncSkipped {
  gamOrderId: string;
  gamOrderName: string;
  reason: AdvertiserSyncSkipReason;
}

export interface AdvertiserSyncWarning {
  /** Child page slug when the warning is about a page; null for discovery-level warnings. */
  slug: string | null;
  gamOrderId?: string;
  reason: AdvertiserSyncWarningReason;
  detail?: string;
}

/** Durable progress record of the last/current sync (table pill + tooltip, Manage summary). */
export interface AdvertiserSyncState {
  status: AdvertiserSyncStatus;
  startedAt: string | null;
  /** Refreshed every 60 s while running; a 'running' state with a stale heartbeat is an interrupted run. */
  heartbeatAt: string | null;
  finishedAt: string | null;
  requestedBy?: string;
  step: AdvertiserSyncStep;
  /** Generation progress: pages generated so far / pages to generate. */
  current: number;
  total: number;
  created: string[];
  linked: string[];
  unlinked: string[];
  skipped: AdvertiserSyncSkipped[];
  warnings: AdvertiserSyncWarning[];
  errors: string[];
}

/** Per-hub child counts (living, unexpired children only). */
export interface HubCampaignCounts {
  total: number;
  live: number;
  paused: number;
  upcoming: number;
  completed: number;
  generating: number;
}

export interface AdvertiserReportPage {
  /** URL slug — also the Firestore doc id. Frozen at creation. */
  slug: string;
  /** GAM Company ids — the primary advertiser first, then aliases. */
  gamAdvertiserIds: string[];
  /** GAM company name of the primary advertiser, frozen at creation. */
  advertiserName: string;
  /** Display name on the hub. */
  clientLabel: string;
  /** 4-digit passcode — also opens every child campaign page this hub covers. */
  pin: string;
  pinUpdatedAt: string;
  pinUpdatedBy: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  updatedBy?: string;
  revokedAt: string | null;
  revokedBy?: string;
  lastAccessAt: string | null;
  accessCount: number;
  /** Hourly discovery links/creates child pages for newly approved orders. */
  autoSync: boolean;
  /** Clients see APPROVED-not-started campaigns ("Upcoming"). */
  showUpcoming: boolean;
  /** Manually added GAM order ids (may belong to another advertiser — confirmed by the admin). */
  includeOrderIds: string[];
  /** Manually hidden GAM order ids. */
  excludeOrderIds: string[];
  sync: AdvertiserSyncState;
  /** Decorated by the admin API. */
  url: string;
  /** True while a sync is live (in-process, or a 'running' doc with a fresh heartbeat). */
  syncing: boolean;
  /** Present on the list endpoint and the detail endpoint. */
  campaignCounts?: HubCampaignCounts;
}

/** Picker rows grouped by GAM advertiser (GET /candidates). */
export interface AdvertiserCandidateGroup {
  /** '' when GAM did not resolve an advertiser id for these orders. */
  advertiserId: string;
  advertiserName: string;
  counts: Record<AdvertiserCampaignLifecycle, number>;
  orders: ReportableOrder[];
  /** Living advertiser hub already covering this advertiser, if any. */
  existingAdvertiserPageSlug: string | null;
}

/** One discovered order in a hub's roster (GET /:slug). Excluded orders are included, flagged. */
export interface HubRosterRow {
  gamOrderId: string;
  gamOrderName: string;
  advertiserId: string;
  advertiserName: string;
  /** GAM Order.status (upper-cased). */
  status: string;
  startDate: string | null;
  endDate: string | null;
  /** null = never shown (CANCELED / DELETED / DISAPPROVED). */
  lifecycle: AdvertiserCampaignLifecycle | null;
  lineItemCount: number;
  totalImpressionsDelivered: number;
  /** 'include' = manual includeOrderIds entry (may belong to another advertiser). */
  source: 'advertiser' | 'include';
  crossAdvertiser: boolean;
  existingPageSlug: string | null;
  /** The existing page is linked to THIS hub. */
  linkedHere: boolean;
  /** The existing page is linked to another hub (left alone by sync). */
  linkedElsewhere: string | null;
  included: boolean;
  excluded: boolean;
  /** The existing page covers a month slice, not the full flight. */
  monthSlice: boolean;
}

/** A child campaign page as returned by GET /:slug (list-endpoint decorations + hub lifecycle). */
export type AdvertiserChildPage = CampaignReportPage & {
  lifecycle: Exclude<AdvertiserCampaignLifecycle, 'in_work'>;
};

export interface AdvertiserPageDetail {
  page: AdvertiserReportPage;
  children: AdvertiserChildPage[];
  /** null when live GAM discovery failed (the Manage modal falls back to children only). */
  roster: HubRosterRow[] | null;
  rosterWarnings: AdvertiserSyncWarning[];
  rosterTruncated: boolean;
}

export interface CreateAdvertiserPageInput {
  /** Primary GAM advertiser first, then aliases. */
  gamAdvertiserIds: string[];
  clientLabel?: string;
  slug?: string;
  pin?: string;
  showUpcoming?: boolean;
  autoSync?: boolean;
  includeOrderIds?: string[];
  excludeOrderIds?: string[];
  confirmCrossAdvertiser?: boolean;
}

export interface UpdateAdvertiserPageInput {
  clientLabel?: string;
  showUpcoming?: boolean;
  autoSync?: boolean;
  includeOrderIds?: string[];
  excludeOrderIds?: string[];
  gamAdvertiserIds?: string[];
  confirmCrossAdvertiser?: boolean;
}

/** A manual include that belongs to a different GAM advertiser (422 cross_advertiser_include). */
export interface CrossAdvertiserOrder {
  gamOrderId: string;
  advertiserId: string;
  advertiserName: string;
}

export interface AdvertiserRelinkResult {
  linked: string[];
  unlinked: string[];
}

export const getAdvertiserCandidates = async (
  force?: boolean,
): Promise<{ advertisers: AdvertiserCandidateGroup[]; fetchedAt: string }> =>
  apiRequest(`${BASE}/candidates${force ? '?force=1' : ''}`);

export const createAdvertiserPage = async (
  input: CreateAdvertiserPageInput,
): Promise<{ page: AdvertiserReportPage; url: string }> =>
  apiRequest(BASE, { method: 'POST', body: JSON.stringify(input) });

export const listAdvertiserPages = async (): Promise<AdvertiserReportPage[]> => {
  const body = await apiRequest(BASE);
  return body.pages ?? [];
};

export const getAdvertiserPage = async (slug: string): Promise<AdvertiserPageDetail> =>
  apiRequest(`${BASE}/${encodeURIComponent(slug)}`);

export const updateAdvertiserPage = async (
  slug: string,
  patch: UpdateAdvertiserPageInput,
): Promise<{ page: AdvertiserReportPage; relink: AdvertiserRelinkResult | null }> =>
  apiRequest(`${BASE}/${encodeURIComponent(slug)}`, { method: 'PATCH', body: JSON.stringify(patch) });

/** 202 = sync started. Throws 409 `already_syncing` while one runs, 409 `revoked` for revoked pages. */
export const syncAdvertiserPage = async (slug: string): Promise<AdvertiserReportPage> => {
  const body = await apiRequest(`${BASE}/${encodeURIComponent(slug)}/sync`, { method: 'POST' });
  return body.page;
};

export const resetAdvertiserPagePin = async (slug: string, pin?: string): Promise<AdvertiserReportPage> => {
  const body = await apiRequest(`${BASE}/${encodeURIComponent(slug)}/reset-pin`, {
    method: 'POST',
    body: JSON.stringify(pin ? { pin } : {}),
  });
  return body.page;
};

export const revokeAdvertiserPage = async (slug: string): Promise<AdvertiserReportPage> => {
  const body = await apiRequest(`${BASE}/${encodeURIComponent(slug)}/revoke`, { method: 'POST' });
  return body.page;
};

export const restoreAdvertiserPage = async (slug: string): Promise<AdvertiserReportPage> => {
  const body = await apiRequest(`${BASE}/${encodeURIComponent(slug)}/restore`, { method: 'POST' });
  return body.page;
};

/**
 * Hard delete. Children are unlinked first (409 `unlink_failed` = nothing was
 * deleted); `revokeSyncCreatedChildren` also revokes the campaign pages this
 * hub's sync created.
 */
export const deleteAdvertiserPage = async (
  slug: string,
  options: { revokeSyncCreatedChildren?: boolean } = {},
): Promise<{ ok: boolean; unlinked: number; revoked: number }> =>
  apiRequest(`${BASE}/${encodeURIComponent(slug)}`, { method: 'DELETE', body: JSON.stringify(options) });

/** True for the 422 returned when a manual include belongs to another GAM advertiser. */
export const isCrossAdvertiserError = (
  err: unknown,
): err is ApiError & { body: { orders: CrossAdvertiserOrder[] } } => {
  const e = err as ApiError | undefined;
  return !!e && e.status === 422 && e.code === 'cross_advertiser_include' && Array.isArray(e.body?.orders);
};
